/**
 * AI branding policy: vendor model identifiers (anthropic/…, openai/…, …)
 * must NEVER surface in the UI. Wherever a backend payload still carries a
 * model id (agent-platform run history, ops diagnostics, config values),
 * render it through `displayModel` — a neutral, capability-tiered ServicesAI
 * label — instead of the raw string.
 */
export interface LocalizedLabel {
  en: string;
  ar: string;
}

export function displayModel(model?: string | null): LocalizedLabel {
  if (!model?.trim()) return { en: '—', ar: '—' };
  const value = model.toLowerCase();
  if (/(haiku|mini|flash|lite|nano|fast)/.test(value)) {
    return { en: 'ServicesAI fast model', ar: 'نموذج ServicesAI السريع' };
  }
  if (/(opus|ultra|pro\b|o1|max)/.test(value)) {
    return { en: 'ServicesAI advanced model', ar: 'نموذج ServicesAI المتقدم' };
  }
  return { en: 'ServicesAI core model', ar: 'نموذج ServicesAI الأساسي' };
}

/** Vendor-prefixed model ids and bare vendor model names, wherever they hide
 *  inside free text / JSON dumps (config diffs, audit details). */
const MODEL_ID_RE =
  /\b(?:anthropic|openai|google|azure|meta|mistral|xai|cohere|deepseek|bedrock)\/[\w.:-]+|\b(?:claude|gpt|gemini|llama|mixtral|grok)[\w.-]*\b/gi;

/** Replace any vendor model identifier inside arbitrary text with a neutral
 *  ServicesAI marker — used before rendering raw JSON blobs. */
export function scrubModelIds(text: string): string {
  return text.replace(MODEL_ID_RE, 'servicesai-model');
}
